// Send history (F layout) from events/<E>/wa_runs, newest first.
import { Link } from "@tanstack/react-router";
import { useTick } from "@/components/bits";
import { SampleBubble, SenderStrip } from "@/components/shared";
import { dayClock, fmt, whenLabel } from "@/lib/format";
import { useApp } from "@/shell/appData";

export function History() {
  const { runRows, config, cap, next, people } = useApp();
  const now = useTick(30_000);
  const pct = cap.cap ? Math.min(100, (cap.used / cap.cap) * 100) : 0;
  return (
    <div className="page">
      <div className="pghead">
        <div className="t">
          <h1>Send history</h1>
          <p>
            Every batch, who started it and why it stopped. See the next one on{" "}
            <Link className="link" to="/send">
              Send
            </Link>
            .
          </p>
        </div>
      </div>
      <SenderStrip />
      {runRows.length ? (
        <div className="tbl-wrap">
          <table className="tbl" data-testid="runs">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Started</th>
                <th className="hide-sm">Started by</th>
                <th className="r">People</th>
                <th className="r hide-sm">Messages</th>
                <th className="r hide-md">Delivered</th>
                <th className="r hide-md">Failed</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runRows.map((r) => (
                <tr key={r.run.id} data-run={r.run.id}>
                  <td className="nm">{r.label}</td>
                  <td className="sub num" style={{ whiteSpace: "nowrap" }}>
                    {r.run.startedAt ? dayClock(r.run.startedAt) : "—"}
                  </td>
                  <td className="sub hide-sm">{r.run.by || "—"}</td>
                  <td className="r num">{fmt(r.people)}</td>
                  <td className="r num hide-sm">{fmt(r.messages)}</td>
                  <td className="r num hide-md">{fmt(r.delivered)}</td>
                  <td className="r num hide-md" style={r.failed ? { color: "var(--warning)" } : undefined}>
                    {r.failed}
                  </td>
                  <td>
                    {r.state === "running" ? (
                      <span className="badge ok">
                        <span className="sdot running" style={{ width: 6, height: 6, boxShadow: "none" }} />
                        Sending
                      </span>
                    ) : (
                      <span
                        className="muted sm"
                        style={
                          r.state === "stopped" || r.state === "unfinished" ? { color: "var(--warning)" } : undefined
                        }
                      >
                        {r.stateText}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty">
          <h3>No batches yet</h3>
          <p>Each live `wa send` run shows up here.</p>
        </div>
      )}
      <div className="g2e section">
        <div className="card">
          <div className="card-h">
            <h3>Template</h3>
          </div>
          <div className="card-b">
            <div className="mono muted" style={{ marginBottom: 10, wordBreak: "break-all" }}>
              {config?.template ?? "—"}
            </div>
            <SampleBubble p={next.list[0] ?? people[0]} label="Sample · next person in line" />
          </div>
        </div>
        <div className="card">
          <div className="card-h">
            <h3>Daily cap</h3>
            <span className="muted num" style={{ fontSize: 12 }}>
              {fmt(cap.used)} / {fmt(cap.cap)}
            </span>
          </div>
          <div className="card-b">
            <div className="meter lg">
              <i style={{ width: `${pct}%` }} />
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
              {cap.left === 0 && cap.freesAt ? (
                <>
                  Full. A slot frees{" "}
                  <b style={{ color: "var(--foreground)", fontWeight: 500 }}>{whenLabel(cap.freesAt, now)}</b>.
                </>
              ) : cap.used ? (
                `${fmt(cap.left)} left today.`
              ) : (
                "Free."
              )}{" "}
              Unique phones in a rolling 24 hours, from this event’s ledger.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
